// src/services/demoInterceptor.js

import {
  demoOrders,
  demoProducts,
  demoLocations,
  demoUsers,
  demoPatients,
  demoTeamMembers,
  demoManufacturerStats,
  demoSalesRepUsers,
  DEMO_PLACEHOLDER_PDF,
  DEMO_PLACEHOLDER_IMAGE,
  demoPeptideOrders,
  demoPeptideCatalog,
  demoPeptideTierPricing,
  demoPeptideCostData,
  demoPeptideProviderTiers,
  demoPeptidePatients,
  demoLinkedProviders,
  demoPeptideUsers,
  demoPeptidesManufacturerOrgInfo,
  demoPeptidesProviderOrgInfo,
} from "../data/demoData";
import { isUseManufacturerDME } from "../utils/utils";

// ============================================================
// DEMO INTERCEPTOR
// Intercepts all axios requests during demo mode and returns
// fake responses. Uses the adapter override technique so the
// request never leaves the browser and the existing error
// response interceptor is never triggered.
// ============================================================

// Process IDs and URL patterns that should NOT be intercepted in demo mode.
// These hit the real backend so peptide product images render with real
// content (which is roughly consistent across instances). Uploads are still
// intercepted so demo activity doesn't write to the real backend.
const LET_THROUGH_PROCESS_IDS = new Set(["Peptide - Provider - Get File", "Peptide - Manufacturer - Get File"]);

const LET_THROUGH_URL_PATTERNS = [/\/?web\/archive-file\//];

// In-session mutable state (resets on exit)
let demoState = {
  orders: [],
  nextOrderId: 100,
  uploadedFiles: {},
  peptideOrders: [],
  peptideCatalog: [],
  peptideTierPricing: {},
  peptideCostData: {},
  peptideProviderTiers: {},
  peptidePatients: [],
  peptideLinkedProviders: [],
  nextPeptideOrderId: 1000,
  orgInfo: null, // populated per role by setDemoRole
  // Peptide stock (Slice E / Bulk Inventory) — keyed by variant SKU.
  // Seeded on demand from the demo catalog the first time a stock endpoint
  // is called (see seedPeptideStockOnce). Bulk Update mutates this map.
  peptideStock: null,
  peptideInventorySettings: { defaultLowStockThreshold: 20, defaultRestockLeadDays: 14 },
  peptideBulkIdempotency: {},
};

// Demo user IDs whose Recent Activity cursors we wipe on every demo entry
// so the "NEW" markers are always fresh when an evaluator opens demo mode
// (otherwise the cursor persists in localStorage across demo sessions and
// the activity drawer says "you're all caught up").
const DEMO_USER_IDS = [
  "demo-user-provider",
  "demo-user-manufacturer",
  "rep-torres",
  "demo-user-pep-provider",
  "demo-user-pep-mfr",
  "demo-user-pep-patient",
];

function resetDemoActivityCursors() {
  try {
    for (const id of DEMO_USER_IDS) {
      localStorage.removeItem(`dme-since-cursor-${id}`);
      localStorage.removeItem(`peptide-recent-cursor-${id}`);
    }
  } catch {
    // localStorage unavailable (private mode, quota) — non-fatal; the hooks
    // will fall back to in-memory seeds.
  }
}

export function initDemoState() {
  demoState = {
    orders: JSON.parse(JSON.stringify(demoOrders)),
    nextOrderId: 100,
    uploadedFiles: {},
    peptideOrders: JSON.parse(JSON.stringify(demoPeptideOrders)),
    peptideCatalog: JSON.parse(JSON.stringify(demoPeptideCatalog)),
    peptideTierPricing: JSON.parse(JSON.stringify(demoPeptideTierPricing)),
    peptideCostData: JSON.parse(JSON.stringify(demoPeptideCostData)),
    peptideProviderTiers: JSON.parse(JSON.stringify(demoPeptideProviderTiers)),
    peptidePatients: JSON.parse(JSON.stringify(demoPeptidePatients)),
    peptideLinkedProviders: JSON.parse(JSON.stringify(demoLinkedProviders)),
    nextPeptideOrderId: 1000,
    orgInfo: JSON.parse(JSON.stringify(demoPeptidesManufacturerOrgInfo)),
    peptideStock: null,
    peptideInventorySettings: { defaultLowStockThreshold: 20, defaultRestockLeadDays: 14 },
    peptideBulkIdempotency: {},
  };
  resetDemoActivityCursors();
}

export function clearDemoState() {
  demoState = {
    orders: [],
    nextOrderId: 100,
    uploadedFiles: {},
    peptideOrders: [],
    peptideCatalog: [],
    peptideTierPricing: {},
    peptideCostData: {},
    peptideProviderTiers: {},
    peptidePatients: [],
    peptideLinkedProviders: [],
    nextPeptideOrderId: 1000,
    orgInfo: null,
    peptideStock: null,
    peptideInventorySettings: { defaultLowStockThreshold: 20, defaultRestockLeadDays: 14 },
    peptideBulkIdempotency: {},
  };
}

// Seed the in-memory peptide stock map from the demo catalog on first read.
// Uses a deterministic hash of the variant SKU so values look spread out
// (rather than cycling visibly every N rows) but the same demo session
// always shows the same data. Aim: ~15% out-of-stock, ~15% low, rest fine.
function seedPeptideStockOnce() {
  if (demoState.peptideStock != null) return;
  const stock = {};
  const catalog = demoState.peptideCatalog || [];
  for (const product of catalog) {
    for (const variant of product.variants || []) {
      if (!variant || !variant.id) continue;
      // FNV-1a style mix on the SKU string for a stable pseudo-random int.
      let h = 2166136261;
      for (let k = 0; k < variant.id.length; k++) {
        h = (h ^ variant.id.charCodeAt(k)) >>> 0;
        h = (h * 16777619) >>> 0;
      }
      const bucket = h % 100;
      let onHand;
      let reserved = 0;
      if (bucket < 15) {
        onHand = 0; // ~15% out-of-stock
      } else if (bucket < 30) {
        onHand = 5 + (h % 12); // ~15% low (5–16 with threshold 20)
      } else if (bucket < 75) {
        onHand = 40 + (h % 60); // healthy mid-range
        reserved = h % 8;
      } else {
        onHand = 120 + (h % 80); // stocked-up
        reserved = h % 15;
      }
      stock[variant.id] = {
        onHandQty: onHand,
        reservedQty: reserved,
        lowStockThreshold: 20,
        expectedRestockDate: bucket < 15 ? "2026-06-15" : null,
        discontinued: false,
      };
    }
  }
  demoState.peptideStock = stock;
}

export function addDemoOrder(order) {
  demoState.orders.unshift(order);
}

// -- Helpers --

export function extractProcessId(data) {
  if (!data) return null;
  // FormData
  if (typeof data.get === "function") {
    const pid = data.get("processId");
    if (pid) return pid;
  }
  // JSON string
  if (typeof data === "string") {
    try {
      const parsed = JSON.parse(data);
      return parsed.processId || null;
    } catch (e) {
      return null;
    }
  }
  // Plain object
  if (typeof data === "object" && data.processId) {
    return data.processId;
  }
  return null;
}

export function extractParams(data) {
  if (!data) return {};
  if (typeof data.get === "function") {
    try {
      const p = data.get("parameters");
      return p ? JSON.parse(p) : {};
    } catch (e) {
      return {};
    }
  }
  if (typeof data === "string") {
    try {
      return JSON.parse(data).parameters || {};
    } catch (e) {
      return {};
    }
  }
  return data.parameters || {};
}

function extractBody(data) {
  if (!data) return {};
  if (typeof data === "string") {
    try {
      const parsed = JSON.parse(data);
      // Some endpoints wrap under parameters; some send raw object.
      return parsed.parameters || parsed;
    } catch (e) {
      return {};
    }
  }
  if (typeof data.get === "function") {
    try {
      const p = data.get("parameters");
      return p ? JSON.parse(p) : {};
    } catch (e) {
      return {};
    }
  }
  return data.parameters || data;
}

export function stripOrgPrefix(processId) {
  // processIds are formatted as "orgName:Process Name"
  const idx = processId.indexOf(":");
  return idx >= 0 ? processId.substring(idx + 1) : processId;
}

function fakeResponse(data, status = 200) {
  return {
    data,
    status,
    statusText: "OK",
    headers: { "content-type": "application/json" },
    config: {},
  };
}

function fakeAdapter(responseData) {
  return () => Promise.resolve(fakeResponse(responseData));
}

// -- Manufacturer DME suggestion helper --

// Attaches a `suggestedManufacturerDmeProvider` field to an order based on the per-provider
// history map maintained in `demoState.lastManufacturerDmePerProvider`. Resolved against the
// manufacturer's CURRENT credentialedDmes list so edits/removals stay fresh — same pattern
// the backend uses on OrganizationProvider + DoctorOffice.credentialedDmes.
function withSuggestedManufacturerDme(order) {
  if (!order) return order;
  const map = demoState.lastManufacturerDmePerProvider || {};
  const lastName = map[order.organizationId];
  if (!lastName) return order;
  const credentialedDmes = (demoState.orgInfo && demoState.orgInfo.credentialedDmes) || [];
  const match = credentialedDmes.find((d) => d && d.companyName === lastName);
  if (!match) return order;
  return {
    ...order,
    suggestedManufacturerDmeProvider: { key: match, label: match.companyName },
  };
}

// -- Upload & File retrieval helpers --

function handleDemoUpload(params) {
  const { orderId, base64, type } = params;
  const idx = demoState.orders.findIndex((o) => o.id === orderId);
  if (idx < 0) return { output: { order: null } };

  const order = demoState.orders[idx];
  const timestamp = Date.now();
  let fileName;

  if (type === "facesheet") {
    fileName = `demo-facesheet-${timestamp}.pdf`;
    order.faceSheetFile = fileName;
    order.facesheetUploading = false;
  } else if (type === "clinicalDocumentation") {
    fileName = `demo-clinical-${timestamp}.pdf`;
    order.clinicalDocumentationFile = fileName;
    order.clinicalDocumentationUploading = false;
  } else if (type === "wounds") {
    fileName = `demo-wound-${timestamp}.jpg`;
    if (!order.wounds) order.wounds = [];
    order.wounds.push({ fileName });
  } else if (type === "additionalDocuments" || type === "additionalDocs") {
    fileName = `demo-doc-${timestamp}.pdf`;
    if (!order.additionalDocs) order.additionalDocs = [];
    order.additionalDocs.push({
      fileName,
      name: `Document ${(order.additionalDocs.length || 0) + 1}`,
    });
  } else {
    fileName = `demo-${type}-${timestamp}.pdf`;
  }

  // Store actual base64 for later preview
  if (base64 && fileName) {
    const cleanBase64 = base64.replace(/^data:[^;]+;base64,/, "");
    const mimeType = /\.(jpg|jpeg)$/i.test(fileName) ? "image/jpeg" : /\.png$/i.test(fileName) ? "image/png" : "application/pdf";
    demoState.uploadedFiles[fileName] = { base64: cleanBase64, type: mimeType };
  }

  order.updatedAt = new Date().toISOString();

  return { output: { order: { ...order } } };
}

function handleDemoGetFile(params) {
  const { fileName } = params;

  // Check if file was uploaded during this demo session
  const stored = demoState.uploadedFiles[fileName];
  if (stored) {
    return { file: { content: stored.base64, type: stored.type } };
  }

  // Fallback: pre-existing demo order files — return placeholder
  const isImage = /\.(jpg|jpeg|png|gif)$/i.test(fileName);
  if (isImage) {
    return { file: { content: DEMO_PLACEHOLDER_IMAGE, type: "image/png" } };
  }
  return { file: { content: DEMO_PLACEHOLDER_PDF, type: "application/pdf" } };
}

// -- processId-based handlers --
// ProcessId keys MUST match the exact strings in src/services/api.js
// (minus the org prefix which is stripped by stripOrgPrefix)

function findPeptideOrderIdx(orderId) {
  return demoState.peptideOrders.findIndex((o) => o.id === orderId);
}

function newPeptideOrderId() {
  return `demo-pep-order-${demoState.nextPeptideOrderId++}`;
}

// Production peptide views read order.items (array of {variantId, productName,
// dosage, quantity, priceCents, lineTotalCents}) and order.shippingInfo
// ({carrier, trackingNumber}). Demo fixture orders use lineItems +
// unitPriceCents + top-level carrier/trackingNumber for plan-compatibility,
// so we transform on the way out.
// Parse a flat "Street, City, ST 12345" string into { address, city, state, zip }.
// Returns whatever it can extract — components do their own null-safety.
function parseDemoAddress(s) {
  if (!s || typeof s !== "string") return {};
  const parts = s
    .split(",")
    .map((p) => p.trim())
    .filter(Boolean);
  if (parts.length < 3) return { address: s };
  const address = parts.slice(0, parts.length - 2).join(", ");
  const city = parts[parts.length - 2];
  const last = parts[parts.length - 1];
  const m = last.match(/^([A-Z]{2})\s*(\d{5}(?:-\d{4})?)?$/);
  return {
    address,
    city,
    state: m ? m[1] : "",
    zip: m && m[2] ? m[2] : "",
  };
}

// Resolve the catalog product a line item belongs to. Tries variantId first,
// then falls back to a normalized productName match so legacy line items
// (using old variantIds like "var-bpc-157-5mg") still find the new product.
function findDemoCatalogProduct(line) {
  const cat = demoState.peptideCatalog || [];
  if (!line) return null;
  if (line.variantId) {
    for (const p of cat) {
      if ((p.variants || []).some((v) => v.id === line.variantId)) return p;
    }
  }
  if (line.productName) {
    const norm = (s) => (s || "").toLowerCase().replace(/[\s\-_+]/g, "");
    const target = norm(line.productName);
    for (const p of cat) {
      if (norm(p.name) === target) return p;
    }
  }
  return null;
}

function shapePeptideOrder(order) {
  if (!order) return order;
  const lineItems = order.lineItems || order.items || [];
  const items = lineItems.map((li) => {
    const product = findDemoCatalogProduct(li);
    const productVariants = product?.variants || [];
    // If the line's variantId is stale (not in the current catalog), remap to
    // the catalog's first variant for that product so the dropdown has a
    // matching option to highlight.
    const variantId = productVariants.some((v) => v.id === li.variantId) ? li.variantId : productVariants[0]?.id || li.variantId;
    return {
      productId: product?.id,
      variantId,
      productName: product?.name || li.productName,
      dosage: li.dosage || li.size,
      quantity: li.quantity,
      priceCents: li.priceCents ?? li.unitPriceCents,
      lineTotalCents: li.lineTotalCents,
      formType: li.formType || "active",
    };
  });
  const shippingInfo =
    order.shippingInfo ||
    (order.carrier || order.trackingNumber ? { carrier: order.carrier, trackingNumber: order.trackingNumber } : undefined);
  // Production reads order.paymentReceipt = { fileName, uploadedAt }; the demo
  // seed and upload handler track it as a flat fileName string for simplicity.
  const paymentReceipt =
    order.paymentReceipt ||
    (order.paymentReceiptFile
      ? { fileName: order.paymentReceiptFile, uploadedAt: order.updatedAt || order.createdAt }
      : undefined);
  // Production reads order.shipping = { firstName, lastName, address, city,
  // state, zip, phone, email }; demo seed uses flat patientX fields plus a
  // single shippingAddress string. Synthesize the object shape so the order
  // detail view's contact / shipping panels render fully populated.
  const parsed = parseDemoAddress(order.shippingAddress);
  const shipping = order.shipping || {
    firstName: order.patientFirstName || "",
    lastName: order.patientLastName || "",
    email: order.patientEmail || "",
    phone: order.patientPhone || "",
    address: parsed.address || "",
    city: parsed.city || "",
    state: parsed.state || "",
    zip: parsed.zip || "",
  };
  return {
    ...order,
    items,
    lineItems, // keep for forward-compat
    fulfillmentType: order.fulfillmentType || "shipped",
    shipping,
    ...(shippingInfo ? { shippingInfo } : {}),
    ...(paymentReceipt ? { paymentReceipt } : {}),
  };
}

// Returns a deep-copy of demoManufacturerStats with the per-user ownership
// flags rewritten so a manufacturer-role demo (Karen Patel / Avery Singh)
// sees a populated "My Accounts" view in AccountsView. AccountsView's
// view filter ("My Accounts" / "Rep Accounts") keys off item.isMyOrg /
// item.isMyRepOrg respectively. The seed fixture only marks Torres'
// direct orgs (org-sunshine, org-palm), so for the manufacturer demos we
// split the 14-account list roughly in half: first half is "directly
// owned" (isMyOrg), second half is "sub-rep" (isMyRepOrg). We never
// mutate demoManufacturerStats itself — clones are returned so role
// switches and re-fetches stay deterministic.
function cloneStatsForManufacturerView() {
  const stats = JSON.parse(JSON.stringify(demoManufacturerStats));
  const halfway = Math.ceil(stats.length / 2);
  stats.forEach((org, idx) => {
    org.isMyOrg = idx < halfway;
    org.isMyRepOrg = idx >= halfway;
  });
  return stats;
}

const processHandlers = {
  "Sales - List My Team": (_, demoRole) => {
    const currentUser = demoPeptideUsers[demoRole]?.user || demoUsers[demoRole]?.user;
    const users = JSON.parse(JSON.stringify(demoSalesRepUsers));
    if (!currentUser?.id) return { output: { users: [] } };

    // The peptide sales persona uses its own demo id; attach seeded root reps
    // to it so the session-scoped process mirrors the real descendant query.
    if (demoRole === "peptidesSalesDashboard") {
      users.forEach((user) => {
        if (user.custom && user.custom.invitedBy === "rep-torres") user.custom.invitedBy = currentUser.id;
      });
    }

    const descendants = [];
    const visited = new Set([currentUser.id]);
    const queue = users.filter((user) => user.custom?.invitedBy === currentUser.id);
    while (queue.length > 0) {
      const user = queue.shift();
      if (!user?.id || visited.has(user.id)) continue;
      visited.add(user.id);
      descendants.push(user);
      queue.push(...users.filter((candidate) => candidate.custom?.invitedBy === user.id));
    }
    return { output: { users: descendants } };
  },
  "Order - Provider - List": () => {
    return { output: { orders: [...demoState.orders] } };
  },
  "Order - Manufacturer - List": () => {
    // Manufacturer kanban: enrich each order with `suggestedManufacturerDmeProvider` from the
    // per-provider history map. Mirrors the backend's resolveSuggestion lookup. Provider-side
    // never sees this field.
    return { output: { orders: demoState.orders.map((o) => withSuggestedManufacturerDme(o)) } };
  },
  // getOrder uses "Order - {type} - Get Order" (api.js:1464)
  // OrderDrawer reads response.data.output.order (line 115)
  "Order - Provider - Get Order": (params) => {
    const order = demoState.orders.find((o) => o.id === params.orderId);
    return { output: { order: order || demoState.orders[0] } };
  },
  "Order - Manufacturer - Get Order": (params) => {
    const order = demoState.orders.find((o) => o.id === params.orderId);
    return { output: { order: withSuggestedManufacturerDme(order || demoState.orders[0]) } };
  },
  // ModalEditOrder reads response.data.output.products (line 527)
  // Products must be an array of STRINGS (e.g., "Collagen - 4x4 - A6021")
  // ModalEditOrder calls p.toLowerCase() on each product (line 218)
  "Order - Provider - Get Products": () => {
    return { output: { products: demoProducts.map((p) => p.name) } };
  },
  // createOrder uses "Order - Provider - Create" (api.js:1508)
  "Order - Provider - Create": (params) => {
    const orderId = "demo-order-" + demoState.nextOrderId++;
    const timestamp = Date.now();

    // Store uploaded file base64s if provided
    const files = {};
    if (params.faceSheet) {
      const fn = `demo-facesheet-${timestamp}.pdf`;
      files.faceSheetFile = fn;
      demoState.uploadedFiles[fn] = {
        base64: params.faceSheet.replace(/^data:[^;]+;base64,/, ""),
        type: "application/pdf",
      };
    }
    if (params.clinicalDocumentation) {
      const fn = `demo-clinical-${timestamp}.pdf`;
      files.clinicalDocumentationFile = fn;
      demoState.uploadedFiles[fn] = {
        base64: params.clinicalDocumentation.replace(/^data:[^;]+;base64,/, ""),
        type: "application/pdf",
      };
    }
    if (params.woundPicture) {
      const fn = `demo-wound-${timestamp}.jpg`;
      files.wounds = [{ fileName: fn }];
      demoState.uploadedFiles[fn] = {
        base64: params.woundPicture.replace(/^data:[^;]+;base64,/, ""),
        type: "image/jpeg",
      };
    }

    const newOrder = {
      id: orderId,
      orderUuid: params.orderUuid || `demo-uuid-${timestamp}`,
      status: "pending",
      patientFirstName: "New",
      patientLastName: "Patient",
      organizationId: "demo-org",
      organizationName: "Miami Wound Care Center",
      dateOfService: params.dateOfService || new Date().toISOString(),
      createdOn: new Date().toISOString(),
      lastStatusChange: timestamp,
      assignedTo: null,
      faceSheetFile: files.faceSheetFile || null,
      clinicalDocumentationFile: files.clinicalDocumentationFile || null,
      facesheetUploading: false,
      clinicalDocumentationUploading: false,
      productDispensed: [],
      wounds: files.wounds || [],
      woundAssessment: [],
      isArchived: false,
      isUrgent: false,
      isSuspended: false,
      providerComments: 0,
      manufacturerComments: 0,
      additionalIvrInfo: null,
    };
    demoState.orders.unshift(newOrder);
    // Return format: no output.error means success in ModalCreateOrder
    return { output: {} };
  },
  // updateOrder sends params: { order: fullOrderObject, organizationId }
  "Order - Provider - Update": (params) => {
    const orderData = params.order || params;
    const orderId = orderData.id || params.orderId;
    const idx = demoState.orders.findIndex((o) => o.id === orderId);
    if (idx >= 0) {
      demoState.orders[idx] = { ...demoState.orders[idx], ...orderData, updatedAt: new Date().toISOString() };
    }
    return { success: true };
  },
  "Order - Manufacturer - Update": (params) => {
    const orderData = params.order || params;
    const orderId = orderData.id || params.orderId;
    const idx = demoState.orders.findIndex((o) => o.id === orderId);
    if (idx >= 0) {
      demoState.orders[idx] = { ...demoState.orders[idx], ...orderData, updatedAt: new Date().toISOString() };
    }
    return { success: true };
  },
  // submitOrder sends params: { orderId, status, pin, consentData }
  // OrderActions reads res.data.output.order (line 335)
  "Order - Provider - Submit Order": (params) => {
    const newStatus = params.status || "submitted";
    const idx = demoState.orders.findIndex((o) => o.id === params.orderId);
    if (idx >= 0) {
      demoState.orders[idx].status = newStatus;
      demoState.orders[idx].updatedAt = new Date().toISOString();
      if (!demoState.orders[idx].history) demoState.orders[idx].history = [];
      demoState.orders[idx].history.push({
        field: "status",
        value: newStatus,
        date: new Date().toISOString(),
        editorLabel: demoUsers[currentDemoRole]?.user
          ? `${demoUsers[currentDemoRole].user.firstName} ${demoUsers[currentDemoRole].user.lastName}`
          : "System",
      });
      return { output: { order: { ...demoState.orders[idx] } } };
    }
    return { output: { order: null } };
  },
  // Manufacturer status changes — OrderActions reads res.data.output.order (line 273)
  "Order - Manufacturer - Update Status": (params) => {
    const idx = demoState.orders.findIndex((o) => o.id === params.orderId);
    if (idx < 0 || !params.status) return { output: { order: null } };

    const targetOrder = demoState.orders[idx];

    // Mirror the backend hard stop: a UMD order can't transition to "approved" or
    // "rejected" without a canonical credentialed-DME pick. The backend throws on this;
    // the demo returns a thrown-shape result so callers behave consistently across modes.
    // Canonicalize the pick against the manufacturer's current credentialedDmes — silently
    // accepting a non-credentialed pick here would mislead users testing the flow.
    if (params.status === "approved" || params.status === "rejected") {
      if (isUseManufacturerDME(targetOrder)) {
        const credentialedDmes = (demoState.orgInfo && demoState.orgInfo.credentialedDmes) || [];
        const incomingName = params.manufacturerDmeProvider?.key?.companyName || "";
        const storedName = targetOrder.manufacturerDmeProvider?.key?.companyName || "";
        const canonicalIncoming = incomingName ? credentialedDmes.find((d) => d && d.companyName === incomingName) : null;
        const canonicalStored = storedName ? credentialedDmes.find((d) => d && d.companyName === storedName) : null;
        const finalPick = canonicalIncoming || canonicalStored;
        if (!finalPick) {
          // Throw shape that mirrors how axios surfaces 4xx responses in the real client.
          const message =
            params.status === "approved"
              ? "manufacturerDmeProvider is required when approving an order that uses 'Use Manufacturer's DME'"
              : "manufacturerDmeProvider is required to reject an order that uses 'Use Manufacturer's DME'";
          const err = new Error(message);
          err.response = { status: 400, data: { error: message } };
          throw err;
        }
        // Persist the canonical pick (not the raw client payload) and update last-used.
        targetOrder.manufacturerDmeProvider = { key: finalPick, label: finalPick.companyName };
        const providerOrgId = targetOrder.organizationId;
        if (providerOrgId) {
          if (!demoState.lastManufacturerDmePerProvider) demoState.lastManufacturerDmePerProvider = {};
          demoState.lastManufacturerDmePerProvider[providerOrgId] = finalPick.companyName;
        }
      } else if (params.manufacturerDmeProvider?.key?.companyName) {
        // Non-UMD order but a pick was supplied — accept it canonically for parity.
        const credentialedDmes = (demoState.orgInfo && demoState.orgInfo.credentialedDmes) || [];
        const canonical = credentialedDmes.find((d) => d && d.companyName === params.manufacturerDmeProvider.key.companyName);
        if (canonical) {
          targetOrder.manufacturerDmeProvider = { key: canonical, label: canonical.companyName };
        }
      }
    }

    targetOrder.status = params.status;
    targetOrder.updatedAt = new Date().toISOString();
    if (!targetOrder.history) targetOrder.history = [];
    targetOrder.history.push({
      field: "status",
      value: params.status,
      date: new Date().toISOString(),
      editorLabel: demoUsers[currentDemoRole]?.user
        ? `${demoUsers[currentDemoRole].user.firstName} ${demoUsers[currentDemoRole].user.lastName}`
        : "System",
    });
    return { output: { order: { ...targetOrder } } };
  },
  "Order - Provider - Upload Document": (params) => {
    return handleDemoUpload(params);
  },
  "Order - Manufacturer - Upload Document": (params) => {
    return handleDemoUpload(params);
  },
  "Order - Provider - Get File": (params) => {
    return handleDemoGetFile(params);
  },
  "Order - Manufacturer - Get File": (params) => {
    return handleDemoGetFile(params);
  },
  "Order - Provider - Cancel Order": (params) => {
    const idx = demoState.orders.findIndex((o) => o.id === params.orderId);
    if (idx >= 0) demoState.orders.splice(idx, 1);
    return { success: true };
  },
  "Order - Manufacturer - Cancel Order": (params) => {
    const idx = demoState.orders.findIndex((o) => o.id === params.orderId);
    if (idx >= 0) demoState.orders.splice(idx, 1);
    return { success: true };
  },
  "Order - Manufacturer - Add Note": (params) => {
    const idx = demoState.orders.findIndex((o) => o.id === params.orderId);
    if (idx >= 0) {
      if (!demoState.orders[idx].notes) demoState.orders[idx].notes = [];
      demoState.orders[idx].notes.push({
        note: params.note || params.text || "",
        creatorLabel: demoUsers[currentDemoRole]?.user
          ? `${demoUsers[currentDemoRole].user.firstName} ${demoUsers[currentDemoRole].user.lastName}`
          : "Karen Patel",
        date: new Date().toISOString(),
      });
    }
    return { success: true };
  },
  "Order - Manufacturer - Set Urgent": (params) => {
    const idx = demoState.orders.findIndex((o) => o.id === params.orderId);
    if (idx >= 0) demoState.orders[idx].urgent = params.urgent ?? true;
    return { success: true };
  },
  "Order - Provider - Set Urgent": (params) => {
    const idx = demoState.orders.findIndex((o) => o.id === params.orderId);
    if (idx >= 0) demoState.orders[idx].urgent = params.urgent ?? true;
    return { success: true };
  },
  "Order - Provider - Add Comment": (params) => {
    const idx = demoState.orders.findIndex((o) => o.id === params.orderId);
    if (idx >= 0) {
      if (!demoState.orders[idx].comments) demoState.orders[idx].comments = [];
      demoState.orders[idx].comments.push({
        date: new Date().toISOString(),
        comment: params.comment || params.text || "",
        creator: demoUsers[currentDemoRole]?.user?.id || "demo-user",
        creatorLabel: demoUsers[currentDemoRole]?.user
          ? `${demoUsers[currentDemoRole].user.firstName} ${demoUsers[currentDemoRole].user.lastName}`
          : "Demo User",
      });
    }
    return { success: true };
  },
  "Order - Manufacturer - Add Comment": (params) => {
    const idx = demoState.orders.findIndex((o) => o.id === params.orderId);
    if (idx >= 0) {
      if (!demoState.orders[idx].comments) demoState.orders[idx].comments = [];
      demoState.orders[idx].comments.push({
        date: new Date().toISOString(),
        comment: params.comment || params.text || "",
        creator: demoUsers[currentDemoRole]?.user?.id || "demo-user",
        creatorLabel: demoUsers[currentDemoRole]?.user
          ? `${demoUsers[currentDemoRole].user.firstName} ${demoUsers[currentDemoRole].user.lastName}`
          : "Demo User",
        from: "manufacturer",
      });
    }
    return { success: true };
  },
  "Order - Provider - Create Reminder": () => ({ success: true }),
  "Order - Provider - Cancel Reminder": () => ({ success: true }),
  "Order - Provider - Set Delivered": () => ({ success: true }),
  "Order - Manufacturer - Set Delivered": () => ({ success: true }),

  // Sales / Manufacturer stats — actual processId is "Order - Manufacturer - Stats" (api.js:1784)
  // useOrdersStats expects { output: { stats: [...orgs with nested orders] } }
  "Order - Manufacturer - Stats": () => {
    // For peptide manufacturer demo, return the peptide linked provider list
    // so usePeptidePricing's provider fetch shows our seeded clinics.
    if (currentDemoRole === "peptidesManufacturer") {
      // Provide DME-side stats too so the Sales Dashboard's Accounts/My Accounts /
      // Performance / Revenue tabs populate. Reuse demoManufacturerStats with the
      // ownership flags rewritten so the manufacturer (Avery Singh) "owns" roughly
      // half of the accounts and the rest are sub-rep accounts.
      return { output: { stats: cloneStatsForManufacturerView() } };
    }
    if (currentDemoRole === "manufacturer") {
      // Karen Patel's view: owner of the first half of accounts, sub-rep accounts
      // for the rest. Rewriting per-user flags on a deep-copy keeps the Torres
      // (salesDashboard) seed fixture untouched.
      return { output: { stats: cloneStatsForManufacturerView() } };
    }
    // salesDashboard (Torres) / other roles: pass through unchanged — the post-
    // process loop in demoData.js already set Torres-perspective flags
    // (org-sunshine + org-palm = isMyOrg, others = isMyRepOrg).
    return { output: { stats: demoManufacturerStats } };
  },
  // Organization update (api.js:747). savePeptideData calls this with the
  // merged org including new peptideCatalog / peptideTierPricing /
  // peptideProviderTiers. We mirror those onto demoState so the change
  // sticks across role switches and view re-fetches WITHIN the demo session.
  // The real backend is never touched.
  "Order - Provider - Update Organization": (params) => {
    const data = params?.data || {};
    if (data.peptideCatalog) demoState.peptideCatalog = data.peptideCatalog;
    if (data.peptideTierPricing) demoState.peptideTierPricing = data.peptideTierPricing;
    if (data.peptideProviderTiers) demoState.peptideProviderTiers = data.peptideProviderTiers;
    return { success: true };
  },
  // User plan check (useAuth.js:checkUserPlan)
  "User Plan": () => ({ output: { maxNotes: -1, totalNotes: 0, trialEndDate: null } }),

  // Patient panel — Patients page calls these. Peptide demo serves peptide
  // patients (Maria, Jonathan, Aisha, etc.); DME demo serves the wound-care
  // patient roster (Margaret, Robert, Dorothy, etc.). Both stay in demoState.
  "Search Patients": (params) => {
    const text = (params?.text || "").toLowerCase().trim();
    const isPeptideRole = currentDemoRole && currentDemoRole.startsWith("peptides");
    const sourcePatients = isPeptideRole ? demoState.peptidePatients : demoPatients;
    const users = (sourcePatients || [])
      .map((p) => ({
        ...p,
        mobilePhone: p.mobilePhone || p.phone || "",
        groups: p.groups || ["patient"],
      }))
      .filter((u) => {
        if (!text) return true;
        const name = `${u.firstName || ""} ${u.lastName || ""}`.toLowerCase();
        return name.includes(text) || (u.email || "").toLowerCase().includes(text);
      });
    return { output: { users } };
  },
  "Clinical Profile - List": () => {
    const isPeptideRole = currentDemoRole && currentDemoRole.startsWith("peptides");
    const profiles = isPeptideRole ? demoState.peptidePatients : [];
    return { output: { profiles } };
  },

  // ── PEPTIDE CATALOG & PRICING ──
  "Peptide - Provider - Get Catalog": () => ({
    output: {
      catalog: demoState.peptideCatalog,
      tierPricing: demoState.peptideTierPricing,
      providerTiers: demoState.peptideProviderTiers,
    },
  }),
  "Peptide - Patient - Get Catalog": () => {
    // Patients see only tier W (retail) prices.
    const filtered = {};
    Object.entries(demoState.peptideTierPricing).forEach(([k, v]) => {
      if (v.W != null) filtered[k] = { W: v.W };
    });
    return {
      output: {
        catalog: demoState.peptideCatalog,
        tierPricing: filtered,
      },
    };
  },
  "Peptide - Manufacturer - Save Cost": (params) => {
    if (params.variantId != null && params.costCents != null) {
      demoState.peptideCostData[params.variantId] = params.costCents;
    }
    return { success: true };
  },
  // Slice E — single-variant stock (ModalStockEditor reads through here).
  // Returns the manufacturer projection: full row with onHandQty + reserved.
  "Peptide Stock - Get": (params) => {
    seedPeptideStockOnce();
    const skus = (params && params.skus) || [];
    const stockMap = demoState.peptideStock || {};
    const out = {};
    const keys = skus.length > 0 ? skus : Object.keys(stockMap);
    for (const sku of keys) {
      const row = stockMap[sku] || null;
      out[sku] = row
        ? {
            onHandQty: row.onHandQty || 0,
            reservedQty: row.reservedQty || 0,
            availableQty: Math.max(0, (row.onHandQty || 0) - (row.reservedQty || 0)),
            lowStockThreshold: row.lowStockThreshold ?? 20,
            expectedRestockDate: row.expectedRestockDate || null,
            discontinued: !!row.discontinued,
            status: row.discontinued
              ? "discontinued"
              : (row.onHandQty || 0) - (row.reservedQty || 0) <= 0
                ? row.expectedRestockDate
                  ? "back_ordered"
                  : "out_of_stock"
                : (row.onHandQty || 0) - (row.reservedQty || 0) <= (row.lowStockThreshold ?? 20)
                  ? "low_stock"
                  : "in_stock",
          }
        : {
            onHandQty: 0,
            reservedQty: 0,
            availableQty: 0,
            lowStockThreshold: demoState.peptideInventorySettings.defaultLowStockThreshold,
            expectedRestockDate: null,
            discontinued: false,
            status: "out_of_stock",
          };
    }
    return { output: { stock: out, role: "manufacturer" } };
  },
  // Bulk Inventory page — all variants in the manufacturer's catalog.
  "Peptide Stock - Get All": () => {
    seedPeptideStockOnce();
    const stockMap = demoState.peptideStock || {};
    const settings = demoState.peptideInventorySettings;
    const out = [];
    for (const product of demoState.peptideCatalog || []) {
      for (const variant of product.variants || []) {
        if (!variant || !variant.id) continue;
        const row = stockMap[variant.id] || {};
        out.push({
          sku: variant.id,
          productId: product.id,
          productName: product.name || "",
          dosage: variant.dosage || "",
          onHandQty: row.onHandQty || 0,
          reservedQty: row.reservedQty || 0,
          lowStockThreshold: row.lowStockThreshold ?? settings.defaultLowStockThreshold,
          expectedRestockDate: row.expectedRestockDate || null,
          discontinued: !!row.discontinued,
        });
      }
    }
    return { output: { stock: out } };
  },
  // Set + Receive bulk save. Honors receivedQty (delta-add) and idempotencyKey
  // replay semantics so the demo behaves like the real backend.
  "Peptide Stock - Bulk Update": (params) => {
    seedPeptideStockOnce();
    const rows = (params && params.rows) || [];
    const key = params && params.idempotencyKey;
    if (key && demoState.peptideBulkIdempotency[key]) {
      return { output: { ...demoState.peptideBulkIdempotency[key], replayed: true } };
    }
    const audits = [];
    for (const r of rows) {
      if (!r || !r.sku) continue;
      const current = demoState.peptideStock[r.sku] || {
        onHandQty: 0,
        reservedQty: 0,
        lowStockThreshold: demoState.peptideInventorySettings.defaultLowStockThreshold,
        expectedRestockDate: null,
        discontinued: false,
      };
      const next = { ...current };
      if (r.receivedQty != null && r.onHandQty != null) {
        throw new Error("row may carry onHandQty OR receivedQty, not both");
      }
      if (r.receivedQty != null) {
        next.onHandQty = (current.onHandQty || 0) + (parseInt(r.receivedQty, 10) || 0);
      } else if (r.onHandQty != null) {
        next.onHandQty = parseInt(r.onHandQty, 10) || 0;
      }
      if (r.lowStockThreshold != null) next.lowStockThreshold = parseInt(r.lowStockThreshold, 10) || 0;
      if (r.expectedRestockDate !== undefined) next.expectedRestockDate = r.expectedRestockDate || null;
      if (r.discontinued !== undefined) next.discontinued = r.discontinued === true;
      demoState.peptideStock[r.sku] = next;
      audits.push({ peptideSku: r.sku, action: "adjust", qtyDelta: next.onHandQty - (current.onHandQty || 0) });
    }
    const response = { changedCount: audits.length, audits };
    if (key) demoState.peptideBulkIdempotency[key] = response;
    return { output: response };
  },
  // Set-mode decrement preview. Demo has no real orders attached to stock,
  // so always return "0 lines impacted" — the FE will skip the warning.
  "Peptide Stock - Decrement Impact - Get": () => ({
    output: { affectedLineCount: 0, affectedOrderIds: [] },
  }),
  // Inventory Settings — org-level defaults consumed by ModalStockEditor and
  // the Bulk Inventory grid when hydrating variants with no saved threshold.
  "Peptide Inventory Settings - Get": () => ({
    output: { ...demoState.peptideInventorySettings },
  }),
  "Peptide Inventory Settings - Save": (params) => {
    const threshold =
      params && params.defaultLowStockThreshold != null
        ? Math.max(0, parseInt(params.defaultLowStockThreshold, 10) || 0)
        : demoState.peptideInventorySettings.defaultLowStockThreshold;
    const leadRaw = params && params.defaultRestockLeadDays;
    const leadDays = leadRaw == null || leadRaw === "" ? null : Math.max(0, parseInt(leadRaw, 10) || 0);
    demoState.peptideInventorySettings = {
      defaultLowStockThreshold: threshold,
      defaultRestockLeadDays: leadDays,
    };
    return { output: { ...demoState.peptideInventorySettings } };
  },
  "Upload Media": (params) => {
    const ts = Date.now();
    const fileName = `demo-media-${ts}.png`;
    if (params.documentContent) {
      const cleanBase64 = params.documentContent.replace(/^data:[^;]+;base64,/, "");
      demoState.uploadedFiles[fileName] = { base64: cleanBase64, type: "image/png" };
    }
    return { output: { fileName } };
  },
  "Order - Manufacturer - Assign Provider Pricing Tier": (params) => {
    if (params.providerOrgId && params.pricingTier) {
      demoState.peptideProviderTiers[params.providerOrgId] = params.pricingTier;
    }
    return { success: true };
  },

  // ── PEPTIDE ORDERS — PROVIDER ──
  "Peptide Order - Provider - List": () => ({
    output: { orders: [...demoState.peptideOrders].map(shapePeptideOrder) },
  }),
  "Peptide Order - Provider - Get": (params) => {
    const idx = findPeptideOrderIdx(params.orderId);
    return { output: { order: shapePeptideOrder(idx >= 0 ? demoState.peptideOrders[idx] : null) } };
  },
  "Peptide Order - Provider - Create": (params) => {
    const orderData = params.order || params;
    const order = {
      id: newPeptideOrderId(),
      organizationId: demoState.peptideOrders[0]?.organizationId || "demo-pep-provider-1",
      organizationName: demoState.peptideOrders[0]?.organizationName || "Demo Provider",
      ...orderData,
      status: orderData.status || "submitted",
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      statusHistory: [{ status: orderData.status || "submitted", date: new Date().toISOString() }],
    };
    // Demo: any Zelle order without a receipt gets a fake one auto-attached so
    // the audience never sees an empty "upload your receipt" state.
    if (order.paymentMethod === "zelle" && !order.paymentReceiptFile && !order.paymentReceipt) {
      order.paymentReceiptFile = `demo-zelle-receipt-${order.id}.pdf`;
      order.status = "payment_pending";
    }
    demoState.peptideOrders.unshift(order);
    return { output: { order: shapePeptideOrder(order) } };
  },
  "Peptide Order - Provider - Update": (params) => {
    const orderData = params.order || params;
    const idx = findPeptideOrderIdx(orderData.id || params.orderId);
    if (idx >= 0) {
      demoState.peptideOrders[idx] = {
        ...demoState.peptideOrders[idx],
        ...orderData,
        updatedAt: new Date().toISOString(),
      };
      return { output: { order: shapePeptideOrder(demoState.peptideOrders[idx]) } };
    }
    return { output: { order: null } };
  },
  "Peptide Order - Provider - Update Status": (params) => {
    const idx = findPeptideOrderIdx(params.orderId);
    if (idx >= 0) {
      const order = demoState.peptideOrders[idx];
      order.status = params.status;
      order.updatedAt = new Date().toISOString();
      if (params.cancelReason) order.cancelReason = params.cancelReason;
      if (params.carrier) order.carrier = params.carrier;
      if (params.trackingNumber) order.trackingNumber = params.trackingNumber;
      if (!order.statusHistory) order.statusHistory = [];
      order.statusHistory.push({ status: params.status, date: new Date().toISOString() });
      return { output: { order: shapePeptideOrder(order) } };
    }
    return { output: { order: null } };
  },
  "Peptide Order - Provider - Charge Card": (params) => {
    const idx = findPeptideOrderIdx(params.orderId);
    if (idx >= 0) {
      const order = demoState.peptideOrders[idx];
      order.status = "confirmed";
      order.updatedAt = new Date().toISOString();
      if (!order.statusHistory) order.statusHistory = [];
      order.statusHistory.push({ status: "confirmed", date: new Date().toISOString() });
      return {
        output: {
          success: true,
          transactionId: `demo-txn-${Date.now()}`,
          errorCode: null,
          errorMessage: null,
          order: shapePeptideOrder(order),
        },
      };
    }
    return { output: { success: false, errorCode: "order_not_found", errorMessage: "Order not found", order: null } };
  },
  // Mirror of the provider Charge Card process for manufacturers who opted
  // into "Collect Payment Now". Same demo behavior — confirm the order and
  // return a synthesized transaction id so the success toast fires.
  "Peptide Order - Manufacturer - Charge Card": (params) => {
    const idx = findPeptideOrderIdx(params.orderId);
    if (idx >= 0) {
      const order = demoState.peptideOrders[idx];
      order.status = "confirmed";
      order.updatedAt = new Date().toISOString();
      if (!order.statusHistory) order.statusHistory = [];
      order.statusHistory.push({ status: "confirmed", date: new Date().toISOString() });
      return {
        output: {
          success: true,
          transactionId: `demo-txn-mfr-${Date.now()}`,
          errorCode: null,
          errorMessage: null,
          order: shapePeptideOrder(order),
        },
      };
    }
    return { output: { success: false, errorCode: "order_not_found", errorMessage: "Order not found", order: null } };
  },
  "Peptide Order - Provider - Create Payment Intent": () => ({
    output: { clientSecret: `demo_pi_secret_${Date.now()}` },
  }),
  "Peptide Order - Provider - Upload Payment Receipt": (params) => {
    const idx = findPeptideOrderIdx(params.orderId);
    if (idx >= 0) {
      const ts = Date.now();
      const fileName = `demo-receipt-${ts}.pdf`;
      // api.js:2241 sends params.base64 (base64Content arg)
      const fileContent = params.base64 || params.documentContent || params.fileContent;
      if (fileContent) {
        const cleanBase64 = fileContent.replace(/^data:[^;]+;base64,/, "");
        demoState.uploadedFiles[fileName] = { base64: cleanBase64, type: "application/pdf" };
      }
      const order = demoState.peptideOrders[idx];
      order.paymentReceiptFile = fileName;
      order.status = "payment_pending";
      order.updatedAt = new Date().toISOString();
      if (!order.statusHistory) order.statusHistory = [];
      order.statusHistory.push({ status: "payment_pending", date: new Date().toISOString() });
      return { output: { order: shapePeptideOrder(order) } };
    }
    return { output: { order: null } };
  },

  // ── PEPTIDE ORDERS — MANUFACTURER ──
  "Peptide Order - List": () => ({
    output: { orders: [...demoState.peptideOrders].map(shapePeptideOrder) },
  }),
  "Peptide Order - Get": (params) => {
    const idx = findPeptideOrderIdx(params.orderId);
    return { output: { order: shapePeptideOrder(idx >= 0 ? demoState.peptideOrders[idx] : null) } };
  },
  "Peptide Order - Update": (params) => {
    const orderData = params.order || params;
    const idx = findPeptideOrderIdx(orderData.id || params.orderId);
    if (idx >= 0) {
      demoState.peptideOrders[idx] = {
        ...demoState.peptideOrders[idx],
        ...orderData,
        updatedAt: new Date().toISOString(),
      };
      return { output: { order: shapePeptideOrder(demoState.peptideOrders[idx]) } };
    }
    return { output: { order: null } };
  },
  "Peptide Order - Update Status": (params) => {
    const idx = findPeptideOrderIdx(params.orderId);
    if (idx >= 0) {
      const order = demoState.peptideOrders[idx];
      order.status = params.status;
      order.updatedAt = new Date().toISOString();
      if (params.cancelReason) order.cancelReason = params.cancelReason;
      if (params.carrier) order.carrier = params.carrier;
      if (params.trackingNumber) order.trackingNumber = params.trackingNumber;
      if (!order.statusHistory) order.statusHistory = [];
      order.statusHistory.push({ status: params.status, date: new Date().toISOString() });
      return { output: { order: shapePeptideOrder(order) } };
    }
    return { output: { order: null } };
  },
  "Peptide Order - Manufacturer - Create": (params) => {
    const orderData = params.order || params;
    // Legacy contract: manufacturer orders are drafts for the downstream
    // provider to pay later. The newer in-store sale / "Collect Payment Now"
    // toggle puts the order into payment_pending up-front so the charge
    // endpoint can process it. The real backend (Create script reads
    // `orderData.status || "draft"`) honored this already — but its stock
    // reservation rollup was clobbering payment_pending → awaiting_restock
    // whenever a line lacked stock, blocking the charge. That's fixed in
    // peptideReservations.js (rollup preserves payment_pending baseline).
    // The mock doesn't simulate stock state, so it just honors the submitted
    // status / paymentMethod when the client signals payment-now via any of:
    //   - status === "payment_pending"  (explicit client intent)
    //   - assignType === "instore"     (walk-in counter sale)
    //   - paymentMethod is present     (card/zelle being collected)
    const collectingPaymentNow =
      orderData.status === "payment_pending" || orderData.assignType === "instore" || !!orderData.paymentMethod;
    const initialStatus = collectingPaymentNow ? orderData.status || "payment_pending" : "draft";
    const order = {
      id: newPeptideOrderId(),
      organizationId: orderData.organizationId || "demo-pep-provider-1",
      organizationName: orderData.organizationName || "Demo Provider",
      ...orderData,
      paymentMethod: collectingPaymentNow ? orderData.paymentMethod || null : null,
      status: initialStatus,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      statusHistory: [{ status: initialStatus, date: new Date().toISOString() }],
    };
    demoState.peptideOrders.unshift(order);
    return { output: { order: shapePeptideOrder(order) } };
  },
  "Peptide Order - Create Payment Intent": () => ({
    output: { clientSecret: `demo_pi_secret_${Date.now()}` },
  }),
  "Peptide Order - Manufacturer - Upload Payment Receipt": (params) => {
    const idx = findPeptideOrderIdx(params.orderId);
    if (idx >= 0) {
      const ts = Date.now();
      const fileName = `demo-receipt-${ts}.pdf`;
      // api.js:2241 sends params.base64 (base64Content arg)
      const fileContent = params.base64 || params.documentContent || params.fileContent;
      if (fileContent) {
        const cleanBase64 = fileContent.replace(/^data:[^;]+;base64,/, "");
        demoState.uploadedFiles[fileName] = { base64: cleanBase64, type: "application/pdf" };
      }
      const order = demoState.peptideOrders[idx];
      order.paymentReceiptFile = fileName;
      order.updatedAt = new Date().toISOString();
      return { output: { order: shapePeptideOrder(order) } };
    }
    return { output: { order: null } };
  },

  // ── PEPTIDE ORDERS — PATIENT ──
  "Peptide Order - Patient - List": () => {
    // Demo patient is "demo-pep-pt-1" (Maria Lopez). Filter to her orders;
    // also include any orders created during this session that have no
    // patientId match (defensive — production filters server-side).
    const orders = demoState.peptideOrders.filter((o) => o.patientId === "demo-pep-pt-1").map(shapePeptideOrder);
    return { output: { orders } };
  },
  "Peptide Order - Patient - Get": (params) => {
    const idx = findPeptideOrderIdx(params.orderId);
    return { output: { order: shapePeptideOrder(idx >= 0 ? demoState.peptideOrders[idx] : null) } };
  },
  "Peptide Order - Patient - Create": (params) => {
    const orderData = params.order || params;
    const order = {
      id: newPeptideOrderId(),
      patientId: "demo-pep-pt-1",
      organizationId: "demo-pep-provider-1",
      organizationName: "Coral Gables Wellness Clinic",
      ...orderData,
      paymentMethod: "card",
      status: "submitted",
      tier: "D",
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      statusHistory: [{ status: "submitted", date: new Date().toISOString() }],
    };
    demoState.peptideOrders.unshift(order);
    return { output: { order: shapePeptideOrder(order) } };
  },
  "Peptide Order - Patient - Cancel": (params) => {
    const idx = findPeptideOrderIdx(params.orderId);
    if (idx >= 0) {
      const order = demoState.peptideOrders[idx];
      order.status = "cancelled";
      order.cancelReason = params.cancelReason || "Cancelled by patient";
      order.updatedAt = new Date().toISOString();
      if (!order.statusHistory) order.statusHistory = [];
      order.statusHistory.push({ status: "cancelled", date: new Date().toISOString() });
      return { output: { order: shapePeptideOrder(order) } };
    }
    return { output: { order: null } };
  },
  "Peptide Order - Patient - Create Payment Intent": () => ({
    output: { clientSecret: `demo_pi_secret_${Date.now()}` },
  }),
  // Provider reviews a patient-placed order. Approve captures the Stripe auth
  // hold (payment_authorized → confirmed) and stamps providerApproved so the
  // review gate (isPeptidePatientPaidAwaitingProvider) stops surfacing it and
  // the manufacturer fulfillment lanes pick it up. Mirrors the production
  // backend's capture-on-approval transition.
  "Peptide Order - Patient - Approve": (params) => {
    const idx = findPeptideOrderIdx(params.orderId);
    if (idx >= 0) {
      const order = demoState.peptideOrders[idx];
      order.status = "confirmed";
      order.providerApproved = true;
      order.everPaid = true;
      order.paidAt = new Date().toISOString();
      order.consentTimestamp = params.consentTimestamp || null;
      order.consentVersion = params.consentVersion || null;
      order.updatedAt = new Date().toISOString();
      if (!order.statusHistory) order.statusHistory = [];
      order.statusHistory.push({ status: "confirmed", date: new Date().toISOString() });
      return { output: { success: true, order: shapePeptideOrder(order) } };
    }
    return { output: { success: false, order: null } };
  },
  // Deny voids the auth hold (payment_authorized → denied) and records the
  // reason the patient sees on PatientPeptideHome. No capture occurs.
  "Peptide Order - Patient - Deny": (params) => {
    const idx = findPeptideOrderIdx(params.orderId);
    if (idx >= 0) {
      const order = demoState.peptideOrders[idx];
      order.status = "denied";
      order.providerDeniedReason = params.reason || "Order denied by provider";
      order.updatedAt = new Date().toISOString();
      if (!order.statusHistory) order.statusHistory = [];
      order.statusHistory.push({ status: "denied", date: new Date().toISOString() });
      return { output: { success: true, order: shapePeptideOrder(order) } };
    }
    return { output: { success: false, order: null } };
  },
  "Peptide Order - Patient - Charge Card": (params) => {
    // Demo-only processId — never called in production. Used by the
    // PatientPeptideCheckout demo branch (Task 15) to fake a successful
    // charge after the user submits the demo card form.
    const idx = findPeptideOrderIdx(params.orderId);
    if (idx >= 0) {
      const order = demoState.peptideOrders[idx];
      order.status = "confirmed";
      order.updatedAt = new Date().toISOString();
      if (!order.statusHistory) order.statusHistory = [];
      order.statusHistory.push({ status: "confirmed", date: new Date().toISOString() });
      return {
        output: { success: true, transactionId: `demo-txn-${Date.now()}`, order: shapePeptideOrder(order) },
      };
    }
    return { output: { success: false, order: null } };
  },
};

// -- URL-based handlers (for non-run-process endpoints) --

const urlHandlers = [
  // URL patterns use \/? to handle both "/web/..." and "web/..." (no leading slash)
  // Resolve user-profile across both demoUsers (provider/manufacturer/salesDashboard)
  // and demoPeptideUsers (peptidesProvider/peptidesManufacturer/peptidesPatient).
  // Without this, peptide-role getProfile() returns undefined and Settings'
  // fetchProfile() throws "Cannot read properties of undefined (reading 'user')".
  {
    method: "get",
    pattern: /\/?web\/user-profile/,
    handler: (_, demoRole) => demoPeptideUsers[demoRole] || demoUsers[demoRole || "provider"],
  },
  { method: "put", pattern: /\/?web\/user-profile/, handler: () => ({ success: true }) },
  { method: "get", pattern: /\/?web\/organization-chats/, handler: () => [] },
  { method: "get", pattern: /\/?web\/organization-chat\//, handler: () => ({}) },
  // organization-users is used by listDoctors, listSalesReps, listUsers (api.js:659-662)
  // Components expect response.data.users format
  // Check query params to differentiate: sales-rep filter → return sales rep hierarchy data
  {
    method: "get",
    pattern: /\/?web\/organization-users/,
    handler: (_, demoRole, url) => {
      if (url && url.includes("sales-rep")) {
        // For manufacturer demo roles (DME or peptide), rewrite the master
        // rep's invitedBy to the current user's id so the Sales Dashboard
        // "My Team" tree view builds under them. Without this, Karen
        // Patel / Avery Singh see "0 direct" reports because no seeded rep
        // has invitedBy === their user id. The salesDashboard role
        // (Michael Torres) is unaffected — his existing direct reports
        // already build a proper tree.
        const isManufacturerRole = demoRole === "manufacturer" || demoRole === "peptidesManufacturer";
        const currentUser = demoPeptideUsers[demoRole]?.user || demoUsers[demoRole]?.user;
        if (isManufacturerRole && currentUser?.id) {
          const reps = JSON.parse(JSON.stringify(demoSalesRepUsers));
          reps.forEach((r) => {
            if (r.custom && r.custom.invitedBy == null) {
              r.custom.invitedBy = currentUser.id;
            }
          });
          return { users: reps };
        }
        return { users: demoSalesRepUsers };
      }
      return { users: demoTeamMembers };
    },
  },
  { method: "get", pattern: /\/?web\/query-vertices/, handler: () => ({ result: [{ name: "Demo Organization" }] }) },
  // POST query-vertices used by _getOrgDoc() for reminder templates
  {
    method: "post",
    pattern: /\/?web\/query-vertices/,
    handler: () => {
      const org = demoState.orgInfo ? { ...demoState.orgInfo } : { name: "Demo Organization" };
      if (!org.reminderTemplates) org.reminderTemplates = [];
      if (currentDemoRole === "peptidesManufacturer") {
        // Inject live peptide data from demoState slices so Catalog Manager,
        // Pricing Tier Manager, and Analytics see in-session edits.
        org.peptideCatalog = demoState.peptideCatalog;
        org.peptideTierPricing = demoState.peptideTierPricing;
        org.peptideProviderTiers = demoState.peptideProviderTiers;
        org.peptideCostData = demoState.peptideCostData;
      }
      return { result: [org] };
    },
  },
  // PUT vertex — manufacturer catalog/tier edits merge into demoState so
  // Manage Catalog and Pricing Tier Manager edits stick within the session.
  // Reminder template CRUD and any other field updates silently succeed
  // without persisting (read-only-feeling demo policy).
  {
    method: "put",
    pattern: /\/?web\/vertex/,
    handler: (_, __, ___, rawData) => {
      const body = extractBody(rawData);
      if (currentDemoRole === "peptidesManufacturer") {
        if (Array.isArray(body.peptideCatalog)) {
          demoState.peptideCatalog = body.peptideCatalog;
        }
        if (body.peptideTierPricing && typeof body.peptideTierPricing === "object") {
          demoState.peptideTierPricing = body.peptideTierPricing;
        }
        if (body.peptideProviderTiers && typeof body.peptideProviderTiers === "object") {
          demoState.peptideProviderTiers = body.peptideProviderTiers;
        }
        if (body.peptideCostData && typeof body.peptideCostData === "object") {
          demoState.peptideCostData = body.peptideCostData;
        }
      }
      return { success: true };
    },
  },
  // org-status format must match: { result: { data: [] } } (used by useAuth.js:291-302)
  { method: "get", pattern: /\/?web\/organization-status/, handler: () => ({ result: { data: [] } }) },
  { method: "get", pattern: /\/?web\/shared-database/, handler: () => [] },
  // Ping (used by useJobs for SSE keepalive)
  { method: "get", pattern: /\/?web\/ping/, handler: () => ({ jobs: [], actions: [] }) },
  // SSE endpoint (EventSource tries this, interceptor can't catch EventSource but ping fallback works)
  { method: "get", pattern: /\/sse/, handler: () => ({}) },
  // Media library
  { method: "get", pattern: /\/?web\/media/, handler: () => ({ files: [], folders: [] }) },
  { method: "post", pattern: /\/?web\/media/, handler: () => ({ success: true }) },
  { method: "put", pattern: /\/?web\/media/, handler: () => ({ success: true }) },
  { method: "delete", pattern: /\/?web\/media/, handler: () => ({ success: true }) },
  // File operations (getFile used by media preview)
  {
    method: "get",
    pattern: /\/?web\/file/,
    handler: () => ({ file: { content: DEMO_PLACEHOLDER_PDF, type: "application/pdf" } }),
  },
  // Catalog image upload — return a fake fileName + data URL so the
  // editor stores something. Real bytes aren't persisted across refresh,
  // but the placeholder image renders cleanly.
  {
    method: "post",
    pattern: /\/?web\/file/,
    handler: () => ({
      file: {
        fileName: `demo-image-${Date.now()}.png`,
        url: `data:image/png;base64,${DEMO_PLACEHOLDER_IMAGE}`,
      },
    }),
  },
  // Credentialing (if navigated to)
  { method: "get", pattern: /\/?web\/credentialing/, handler: () => ({ requests: [] }) },
  { method: "post", pattern: /\/?web\/credentialing/, handler: () => ({ success: true }) },
  // Locations — only show for provider role, not sales/manufacturer
  {
    method: "get",
    pattern: /\/?web\/.*location/,
    handler: () => ({ locations: currentDemoRole === "provider" ? demoLocations : [] }),
  },
  { method: "post", pattern: /\/?web\/.*location/, handler: () => ({ success: true }) },
  { method: "put", pattern: /\/?web\/.*location/, handler: () => ({ success: true }) },
  { method: "delete", pattern: /\/?web\/.*location/, handler: () => ({ success: true }) },
];

// -- Main interceptor registration --

let interceptorIds = { api: null, apiAdmin: null };
let currentDemoRole = "provider";

export function setDemoRole(role) {
  currentDemoRole = role;
  // Resolve orgInfo from peptide users first (peptidesProvider /
  // peptidesManufacturer / peptidesPatient) then fall back to the original
  // demoUsers (provider / manufacturer / salesDashboard). If neither has
  // an entry, pick a fixture matching the role flavour — provider-style
  // for provider/peptidesProvider, manufacturer-style for everything
  // else — so query-vertices never returns null.
  const userForRole = demoPeptideUsers[role] || demoUsers[role];
  const isProviderRole = typeof role === "string" && /provider/i.test(role);
  const fallbackOrgInfo = isProviderRole ? demoPeptidesProviderOrgInfo : demoPeptidesManufacturerOrgInfo;
  const orgForRole = userForRole?.orgInfo || fallbackOrgInfo;
  demoState.orgInfo = JSON.parse(JSON.stringify(orgForRole));
}

export function registerDemoInterceptor(apiInstance, apiAdminInstance) {
  // Guard against double-registration — eject any existing interceptors first
  ejectDemoInterceptor(apiInstance, apiAdminInstance);

  // Main API interceptor
  interceptorIds.api = apiInstance.interceptors.request.use((config) => {
    // Check for run-process calls
    if (config.url && config.url.includes("run-process")) {
      const rawProcessId = extractProcessId(config.data);
      if (rawProcessId) {
        const processId = stripOrgPrefix(rawProcessId);
        // Pass-through: real backend serves this (e.g. peptide product images).
        // BUT for fileName params starting with "demo-", short-circuit to a
        // placeholder so seeded demo receipts render without 404s.
        if (LET_THROUGH_PROCESS_IDS.has(processId)) {
          const params = extractParams(config.data);
          const fileName = typeof params?.fileName === "string" ? params.fileName : "";
          // Short-circuit fileNames that look like seeded-demo artifacts
          // (`demo-*` prefix from in-session uploads). Real archive filenames
          // are let through to the backend so the manufacturer's actual
          // catalog images render in demo. If a particular file 404s in a
          // less-seeded environment, PeptideImage handles the error and
          // falls back gracefully.
          if (fileName && fileName.startsWith("demo-")) {
            const isPdf = /\.pdf$/i.test(fileName);
            config.adapter = fakeAdapter({
              output: { file: isPdf ? DEMO_PLACEHOLDER_PDF : DEMO_PLACEHOLDER_IMAGE },
            });
            return config;
          }
          return config;
        }
        const params = extractParams(config.data);
        const handler = processHandlers[processId];
        if (handler) {
          config.adapter = fakeAdapter(handler(params));
          return config;
        }
      }
    }

    // Check URL pattern handlers
    const method = (config.method || "get").toLowerCase();
    const url = config.url || "";
    // Pass-through URL patterns (e.g. /web/archive-file/<id> for peptide images).
    // GET-only — DELETE/PUT/POST stay intercepted so demo activity can never
    // mutate the real backend's archive. Demo-* fileNames are short-circuited
    // to a placeholder so seeded demo files render without 404s.
    if (method === "get") {
      for (const pattern of LET_THROUGH_URL_PATTERNS) {
        if (pattern.test(url)) {
          // Only short-circuit fileNames that look like in-session demo
          // artifacts (`demo-*` prefix). Real archive filenames pass through
          // so the manufacturer's seeded catalog images render — PeptideImage
          // handles 404s gracefully in less-seeded environments.
          const isDemoFile = /\/demo-[\w-]+\./.test(url);
          if (isDemoFile) {
            const isPdf = /\.pdf/i.test(url);
            config.adapter = fakeAdapter({
              file: {
                content: isPdf ? DEMO_PLACEHOLDER_PDF : DEMO_PLACEHOLDER_IMAGE,
                type: isPdf ? "application/pdf" : "image/png",
              },
            });
            return config;
          }
          return config;
        }
      }
    }
    for (const h of urlHandlers) {
      if (h.method === method && h.pattern.test(url)) {
        config.adapter = fakeAdapter(h.handler(extractParams(config.data), currentDemoRole, url, config.data));
        return config;
      }
    }

    // Unmapped endpoint — return generic success and warn
    console.warn("Demo mode: unmapped endpoint", config.method, config.url, extractProcessId(config.data));
    config.adapter = fakeAdapter({ success: true });
    return config;
  });

  // apiAdmin interceptor — intercept everything
  if (apiAdminInstance) {
    interceptorIds.apiAdmin = apiAdminInstance.interceptors.request.use((config) => {
      config.adapter = fakeAdapter([]);
      return config;
    });
  }
}

export function ejectDemoInterceptor(apiInstance, apiAdminInstance) {
  if (interceptorIds.api !== null) {
    apiInstance.interceptors.request.eject(interceptorIds.api);
    interceptorIds.api = null;
  }
  if (interceptorIds.apiAdmin !== null && apiAdminInstance) {
    apiAdminInstance.interceptors.request.eject(interceptorIds.apiAdmin);
    interceptorIds.apiAdmin = null;
  }
}
